// cvd.ts — color-vision-deficiency simulation and the two ways it changes
// what the solver is aiming for.
//
// Simulation uses the Machado, Oliveira & Fernandes (2009) severity-1.0
// matrices, applied in linear sRGB (which is where they are defined).
// Achromatopsia is the luminance projection — the degenerate case where hue
// carries no information whatsoever.
//
// The two criteria that shift:
//
//  1. Every contrast ratio is measured on the colors that viewer actually
//     sees. This is not cosmetic: a protanope loses most of red's luminance
//     contribution, and several bundled presets drop below WCAG AA once you
//     measure them that way — for a protanope, sepia/light puts `onPrimary`
//     on `primary` at 4.10 and tango/dark puts `danger` on the page at 2.72.
//
//  2. Colors that carry *different meanings* have to stay a minimum ΔE apart
//     after simulation. This is the criterion hue distance cannot express:
//     success and danger are 120° apart and a deuteranope sees one color.
//
// What deliberately does NOT change is the WCAG floors themselves. It's
// tempting to step 4.5 up to 7.0 "because they need more help", but the
// thresholds are luminance criteria and colour blindness is a hue-
// discrimination deficit — the number isn't what's wrong, the measurement
// was. Inflating them also breaks the solver outright: an unreachable target
// drives roles to the edge of the gamut where the ratio clips and its
// gradient dies, and the palette lands somewhere much worse than where it
// started. Legibility stays at AA/AAA; discriminability is criterion 2's job.
//
// Why this belongs next to the solver rather than in a CSS filter: a filter
// only shows you the damage. Feeding the simulated colors back into the loss
// function makes the gradient descent *fix* it — a red/green pair that
// collapses under deuteranopia gets pushed apart in lightness, which is the
// one channel every dichromat still has.

import type { OKLCH, RGB } from './oklch';
import { oklchToRgb, rgbToOklab, srgbToLin, linToSrgb, contrastRatio } from './oklch';

export const VISIONS = [
    'normal',
    'protanopia',
    'deuteranopia',
    'tritanopia',
    'achromatopsia',
] as const;
export type Vision = (typeof VISIONS)[number];

export const VISION_LABELS: Record<Vision, string> = {
    normal: 'Normal',
    protanopia: 'Protanopia',
    deuteranopia: 'Deuteranopia',
    tritanopia: 'Tritanopia',
    achromatopsia: 'Achromatopsia',
};

// Shown next to the picker so the choice is legible without a colour-science
// detour. Prevalences are of the male population, where dichromacy is far
// more common (the protan/deutan genes are X-linked).
export const VISION_NOTES: Record<Vision, string> = {
    normal: 'Trichromat targets — WCAG AA/AAA as written.',
    protanopia: 'No red cone (~1% of men). Reds darken toward black.',
    deuteranopia: 'No green cone (~1% of men). Red and green collapse together.',
    tritanopia: 'No blue cone (rare). Blue and green collapse together.',
    achromatopsia: 'No colour at all (very rare). Lightness is the only channel.',
};

// Machado et al. 2009, severity 1.0. Row-major, operating on linear sRGB.
type Matrix = readonly [number, number, number, number, number, number, number, number, number];

const MATRICES: Partial<Record<Vision, Matrix>> = {
    protanopia: [
        0.152286, 1.052583, -0.204868,
        0.114503, 0.786281, 0.099216,
        -0.003882, -0.048116, 1.051998,
    ],
    deuteranopia: [
        0.367322, 0.860646, -0.227968,
        0.280085, 0.672501, 0.047413,
        -0.011820, 0.042940, 0.968881,
    ],
    tritanopia: [
        1.255528, -0.076749, -0.178779,
        -0.078411, 0.930809, 0.147602,
        0.004733, 0.691367, 0.303900,
    ],
};

// Rec. 709 luminance weights — the same ones WCAG's relative luminance uses,
// so an achromatopsia simulation preserves contrast ratios exactly.
const LUMA = [0.2126, 0.7152, 0.0722] as const;

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Project an sRGB color through a color-vision model. `normal` returns the
 * input untouched, so callers can stay branch-free on the hot path.
 */
export function simulateRgb(rgb: RGB, vision: Vision): RGB {
    if (vision === 'normal') return rgb;

    const r = srgbToLin(rgb.r), g = srgbToLin(rgb.g), b = srgbToLin(rgb.b);

    if (vision === 'achromatopsia') {
        const y = LUMA[0] * r + LUMA[1] * g + LUMA[2] * b;
        const s = linToSrgb(clamp01(y));
        return { r: s, g: s, b: s };
    }

    const m = MATRICES[vision]!;
    return {
        r: linToSrgb(clamp01(m[0] * r + m[1] * g + m[2] * b)),
        g: linToSrgb(clamp01(m[3] * r + m[4] * g + m[5] * b)),
        b: linToSrgb(clamp01(m[6] * r + m[7] * g + m[8] * b)),
    };
}

/** OKLCH → the sRGB a viewer with `vision` actually perceives. */
export function simulateOklch(c: OKLCH, vision: Vision): RGB {
    return simulateRgb(oklchToRgb(c), vision);
}

/** WCAG contrast ratio between two roles as seen under `vision`. */
export function visionContrast(a: OKLCH, b: OKLCH, vision: Vision): number {
    return contrastRatio(simulateOklch(a, vision), simulateOklch(b, vision));
}

/**
 * Perceptual distance between two roles as seen under `vision`, measured as
 * Euclidean ΔE in OKLab. This is the criterion hue-distance can't express:
 * under deuteranopia a 120° hue gap between success and danger simulates to
 * near-zero distance, and only a lightness split rescues it.
 */
export function simulatedDistance(a: OKLCH, b: OKLCH, vision: Vision): number {
    const la = rgbToOklab(simulateOklch(a, vision));
    const lb = rgbToOklab(simulateOklch(b, vision));
    return Math.hypot(la.L - lb.L, la.a - lb.a, la.b - lb.b);
}

// Minimum simulated ΔE two roles carrying different meanings must keep.
//
// The dichromat numbers are larger than the trichromat one because a
// trichromat has hue *and* lightness to tell two colors apart, while a
// dichromat is down to whatever survives the projection. Achromatopsia sits
// *lower* than the dichromats on purpose: there the entire budget is
// lightness, and all four semantic colors also owe contrast to the page
// background, which pins them into a narrow lightness band — four roles
// pairwise-separated inside that band is the binding constraint. Demanding
// more than the band can hold just makes the target unreachable.
//
// These are the largest values every bundled preset still satisfies in both
// modes; raising the dichromat floor to 0.15 leaves several pairs short.
export const SEPARATION_MIN: Record<Vision, number> = {
    normal: 0.08,
    protanopia: 0.14,
    deuteranopia: 0.14,
    tritanopia: 0.14,
    achromatopsia: 0.10,
};
